import { Injectable } from '@angular/core';
import { CrudService } from '@marmon/frontend/common';
import { AlgoliaFetch } from '@marmon/frontend/pagination';
import { AlgoliaUser, getName, User } from '@marmon/shared/types/user';

@Injectable({ providedIn: 'root' })
export class UserApiService extends CrudService<User, AlgoliaUser> {
	private readonly displayNames = new Map<string, string>();

	constructor() {
		super('users', { algoliaIndex: 'users' });
	}

	override fetchAlgolia<T extends AlgoliaUser = AlgoliaUser>(
		options: Omit<AlgoliaFetch<T>, 'index' | 'user'>,
	) {
		const user = this.user$.value;
		if (!user) throw Error('UserApiService: User not authenticated');
		return super.fetchAlgolia({
			...options,
		});
	}

	/**
	 * Resolves display names for user ids. Fetches only ids that have not
	 * already been looked up during this session.
	 */
	async displayNamesByIds(ids: string[] = []): Promise<string[]> {
		const missing = ids.filter((id) => id && !this.displayNames.has(id));
		if (missing.length) {
			const users = await this.fetchByIdentities(missing);
			for (const user of users) {
				this.displayNames.set(user.id, getName(user) || user.email);
			}
			for (const id of missing) {
				if (!this.displayNames.has(id)) this.displayNames.set(id, '');
			}
		}

		return ids
			.map((id) => this.displayNames.get(id) ?? '')
			.filter((name) => !!name);
	}
}
